import React, { useState, useEffect } from 'react';
import './Payment.css';
import { useStateValue } from "./StateProvider";
import CheckoutProduct from "./CheckoutProduct";
import { Link, useHistory } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import CurrencyFormat from "react-currency-format";
import { getBasketTotal } from "./reducer";
import axios from './axios';
import { db } from "./firebase";
import { Row, Col, Typography, Button, Steps, Form, Card, Divider, Alert, List } from 'antd';
import { ShoppingOutlined, CreditCardOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

function groupBasketItems(basket) {
  if (!basket) return [];
  const grouped = {};
  basket.forEach(item => {
    if (grouped[item.id]) {
      grouped[item.id].quantity += 1;
    } else {
      grouped[item.id] = { ...item, quantity: 1 };
    }
  });
  return Object.values(grouped);
}

function Payment() {
    const [{ basket, user }, dispatch] = useStateValue();
    const history = useHistory();

    const stripe = useStripe();
    const elements = useElements();

    const [succeeded, setSucceeded] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [disabled, setDisabled] = useState(true);
    const [clientSecret, setClientSecret] = useState("");
    
    // Group basket items
    const groupedItems = groupBasketItems(basket);

    useEffect(() => {
        const getClientSecret = async () => {
            try {
                const response = await axios({
                    method: 'post',
                    url: `/payments/create?total=${getBasketTotal(basket) * 100}`
                });
                setClientSecret(response.data.clientSecret);
            } catch (error) {
                console.error("Error fetching client secret:", error);
            }
        }
        getClientSecret();
    }, [basket]);
    
    const handleSubmit = async (event) => {
        event.preventDefault();
        setProcessing(true);
        setError(null);
        
        if (!stripe || !elements || !clientSecret || !user?.uid) {
            setError("Payment processing error. Missing required payment information.");
            setProcessing(false);
            return;
        }
    
        try {
            const payload = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement)
                }
            });
    
            if (payload.error) {
                setError(payload.error.message);
                setProcessing(false);
            } else if (payload.paymentIntent.status === 'succeeded') {
                db.collection('users')
                  .doc(user.uid)
                  .collection('orders')
                  .doc(payload.paymentIntent.id)
                  .set({
                      basket: basket,
                      amount: payload.paymentIntent.amount,
                      created: payload.paymentIntent.created
                  });
    
                setSucceeded(true);
                setError(null);
                setProcessing(false);
                dispatch({ type: 'EMPTY_BASKET' });
                history.replace('/orders');
            }
        } catch (error) {
            console.error("Error processing payment:", error);
            setError("Payment failed. Please try again.");
            setProcessing(false);
        }
    };
    
    const handleChange = event => {
        setDisabled(event.empty);
        setError(event.error ? event.error.message : null);
    };

    return (
        <div style={{ padding: '40px 24px', maxWidth: '1400px', margin: '0 auto' }}>
            <Steps
                current={1}
                style={{ marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px auto' }}
            >
                <Steps.Step title="Cart" icon={<ShoppingOutlined />} />
                <Steps.Step title="Payment" icon={<CreditCardOutlined />} />
                <Steps.Step title="Complete" icon={<CheckCircleOutlined />} />
            </Steps>
            
            <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
                Checkout ({basket?.length} items)
            </Title>

            <Row justify="center">
                <Col span={24} style={{ maxWidth: '1000px' }}>
                    <Card title="1. Delivery Address" style={{ marginBottom: 24, borderRadius: 8 }}>
                        <Text strong>{user?.email}</Text><br/>
                        <Text>123 React Lane</Text><br/>
                        <Text>Los Angeles, CA</Text>
                    </Card>

                    <Card title="2. Review items and delivery" style={{ marginBottom: 24, borderRadius: 8 }}>
                        {groupedItems.map((item, index) => (
                            <CheckoutProduct
                                key={`${item.id}-${index}`}
                                id={item.id}
                                title={item.title}
                                image={item.image}
                                price={item.price}
                                rating={item.rating}
                                quantity={item.quantity || 1}
                                hideButton
                            />
                        ))}
                    </Card>

                    <Card title="3. Payment Method" style={{ borderRadius: 8 }}>
                        <Form onFinish={handleSubmit} layout="vertical">
                            <Form.Item label="Credit or Debit Card">
                                <div style={{ border: '1px solid #d9d9d9', padding: '12px 14px', borderRadius: '4px', background: 'white' }}>
                                    <CardElement onChange={handleChange} options={{
                                        style: {
                                            base: {
                                                fontSize: '16px',
                                                color: '#32325d',
                                                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                                                '::placeholder': { color: '#aab7c4' }
                                            }
                                        }
                                    }}/>
                                </div>
                            </Form.Item>

                            {error && (
                                <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
                            )}

                            <Divider />

                            <CurrencyFormat
                                renderText={(value) => (
                                    <Title level={4} style={{ marginBottom: 24 }}>Order Total: {value}</Title>
                                )}
                                decimalScale={2}
                                value={getBasketTotal(basket)}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={"$"}
                            />

                            <Button 
                                type="primary" 
                                size="large" 
                                htmlType="submit" 
                                disabled={processing || disabled || succeeded}
                                loading={processing}
                                style={{
                                    height: '44px',
                                    borderRadius: '6px',
                                    fontWeight: 600,
                                    fontSize: '16px'
                                }}
                            >
                                {processing ? 'Processing...' : 'Buy Now'}
                            </Button>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default Payment;
